import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Video } from "@remotion/media";
import { TerminalWindowDark } from "../shared/TerminalWindowDark";
import { CodeTypewriter } from "../shared/CodeTypewriter";
import { FadeInText } from "../shared/FadeInText";
import { DeviceFrame } from "../shared/DeviceFrame";
import { SPRING_CONFIGS } from "../utils/springs";
import type { TemplateProps } from "../Root";

/**
 * Studio Template (based on tuka's CreativeV4CodeTerminal)
 *
 * Layout:
 *   WITH network data: Terminal (left ~42%) + Device mockup (right ~58%)
 *   WITHOUT network data + title: Title centered above device
 *   WITHOUT network data + no title: Device centered only
 * Timeline (10s = 300 frames):
 *   0-25:   Device enters from right with bouncy spring
 *   25-60:  Terminal appears from left with scale morph (if tabs)
 *   60+:    CodeTypewriter animates terminal content
 */
export const Studio: React.FC<TemplateProps> = ({
  videoUrl,
  platform,
  title,
  terminalTabs,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const hasTabs = terminalTabs && terminalTabs.length > 0;
  const hasTitle = title && title.trim().length > 0;

  // --- Device entrance from right ---
  const deviceProgress = spring({
    frame,
    fps,
    delay: 0,
    config: SPRING_CONFIGS.bouncy,
  });
  const deviceScale = interpolate(deviceProgress, [0, 1], [0.8, 1]);
  const deviceOpacity = interpolate(deviceProgress, [0, 1], [0, 1]);
  const deviceSlideX = interpolate(deviceProgress, [0, 1], [150, 0]);

  // --- Terminal entrance with morph/scale ---
  const terminalProgress = spring({
    frame,
    fps,
    delay: 25,
    config: SPRING_CONFIGS.smooth,
  });
  const terminalOpacity = interpolate(terminalProgress, [0, 1], [0, 1]);
  const terminalScale = interpolate(terminalProgress, [0, 1], [0.7, 1]);
  const terminalSlideX = interpolate(terminalProgress, [0, 1], [-80, 0]);

  // --- Title text ---
  const titleProgress = spring({
    frame,
    fps,
    delay: 30,
    config: SPRING_CONFIGS.smooth,
  });
  const titleOpacity = interpolate(titleProgress, [0, 1], [0, 1]);

  // Tab cycling: ~3.3s per tab at 30fps
  const tabDurationFrames = 100;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0F0F1A",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 80px",
        gap: 60,
      }}
    >
      {/* Left side: Terminal + text (only if we have tabs) */}
      {hasTabs && (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 24,
            opacity: terminalOpacity,
            transform: `translateX(${terminalSlideX}px) scale(${terminalScale})`,
          }}
        >
          {/* Title (only if not empty) */}
          {hasTitle && (
            <div style={{ opacity: titleOpacity }}>
              <FadeInText
                delay={35}
                fontSize={28}
                fontWeight={700}
                color="#FFFFFF"
                style={{ lineHeight: 1.2 }}
              >
                {title}
              </FadeInText>
            </div>
          )}

          {/* Tabbed terminal */}
          <div style={{ height: hasTitle ? 500 : 560 }}>
            <TerminalWindowDark
              title="network-trace.json"
              tabs={terminalTabs}
              tabDurationFrames={tabDurationFrames}
              renderTab={(activeIndex) => (
                <CodeTypewriter
                  code={terminalTabs[activeIndex]?.content || ""}
                  charFrames={2}
                  delay={60}
                  fontSize={14}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* No tabs + title: Title text centered above device */}
      {!hasTabs && hasTitle && (
        <div
          style={{
            position: "absolute",
            top: 60,
            left: 0,
            right: 0,
            textAlign: "center",
            opacity: titleOpacity,
          }}
        >
          <FadeInText
            delay={35}
            fontSize={32}
            fontWeight={700}
            color="#FFFFFF"
            style={{ lineHeight: 1.2 }}
          >
            {title}
          </FadeInText>
        </div>
      )}

      {/* Device mockup */}
      <div
        style={{
          flex: hasTabs ? "0 0 auto" : undefined,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: deviceOpacity,
          transform: `scale(${deviceScale}) translateX(${deviceSlideX}px)`,
        }}
      >
        <DeviceFrame platform={platform}>
          <Video
            src={videoUrl}
            muted
            loop
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </DeviceFrame>
      </div>
    </AbsoluteFill>
  );
};
